import { apiClient } from "./api";
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

export interface ContentListItem {
  content_id: string;
  title: string;
  /** @deprecated */
  preview: string;
  content: string;
  word_count: number;
  format: string;
  status: "draft" | "published" | "deleted";
  created_at: string;
  metadata: {
    status: "draft" | "published" | "deleted";
  };
  media_type?: string;
  media_url?: string;
}

export interface TopicContentCreate {
  topic: string;
  format_type?:
    | "essay"
    | "report"
    | "analysis"
    | "opinion"
    | "thought_piece"
    | "research_paper"
    | "editorial";
  length_type?: "short" | "medium" | "long" | "comprehensive" | "custom";
  custom_length?: number;
  user_guidance?: string;
}

export interface MediaContentCreate {
  media_url: string;
  media_type: string;
  format_type?:
    | "essay"
    | "report"
    | "analysis"
    | "opinion"
    | "thought_piece"
    | "research_paper"
    | "editorial";
  length_type?: "short" | "medium" | "long" | "comprehensive" | "custom";
  custom_length?: number;
  user_guidance?: string;
}

export interface ContentEdit {
  title?: string;
  content?: string;
  metadata?: Record<string, any>;
}

export interface ContentFeedbackEdit {
  user_feedback: string;
  content_data?: {
    title: string;
    content: string;
    metadata?: Record<string, any>;
  };
}

export interface ContentSaveOptions {
  title?: string;
  content?: string;
  metadata?: Record<string, any>;
}

export interface ContentResponse {
  data: ContentListItem[];
  pagination: {
    cursor: string | null;
  };
}

export interface ContentEditPreview {
  content_id: string;
  original: {
    title: string;
    content: string;
  };
  edited: {
    title: string;
    content: string;
  };
  feedback: string;
  status: string;
}

// Create content from a topic
export const createTopicContent = async (
  persona_id: string,
  data: TopicContentCreate,
): Promise<{
  content_id: string;
  title: string;
  preview: string;
  word_count: number;
  status: string;
}> => {
  try {
    const response = await apiClient.post("/api/content/create/topic", {
      ...data,
      persona_id,
    });
    return response.data;
  } catch (error) {
    console.error("Error creating topic content:", error);
    throw error;
  }
};

// Create content analyzing media
export const createMediaContent = async (
  persona_id: string,
  data: MediaContentCreate,
): Promise<{
  content_id: string;
  title: string;
  preview: string;
  word_count: number;
  status: string;
  media_type: string;
}> => {
  try {
    const response = await apiClient.post("/api/content/create/media", {
      ...data,
      persona_id,
    });
    return response.data;
  } catch (error) {
    console.error("Error creating media content:", error);
    throw error;
  }
};

// Edit draft content manually
export const editContent = async (
  persona_id: string,
  content_id: string,
  data: ContentEdit,
): Promise<{
  content_id: string;
  title: string;
  preview: string;
  word_count: number;
  status: string;
}> => {
  try {
    const response = await apiClient.put(`/api/content/edit/${content_id}`, {
      persona_id,
      ...data,
    });
    return response.data;
  } catch (error) {
    console.error("Error editing content:", error);
    throw error;
  }
};

// Edit content with AI assistance based on feedback
export const editContentWithFeedback = async (
  persona_id: string,
  content_id: string,
  data: ContentFeedbackEdit,
): Promise<ContentEditPreview> => {
  try {
    const response = await apiClient.post(
      `/api/content/edit-with-feedback/${content_id}`,
      {
        persona_id,
        user_feedback: data.user_feedback,
        // Include content_data if provided (for iterative edits)
        ...(data.content_data ? { content_data: data.content_data } : {}),
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error editing content with feedback:", error);
    throw error;
  }
};

// Save edited content after feedback
export const saveEditedContent = async (
  persona_id: string,
  content_id: string,
  saveOptions?: ContentSaveOptions,
): Promise<{
  content_id: string;
  title: string;
  preview: string;
  word_count: number;
  status: string;
}> => {
  try {
    const response = await apiClient.post(
      `/api/content/save-edited-content/${content_id}`,
      {
        persona_id,
        ...(saveOptions ? { editedContent: saveOptions } : {}),
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error saving edited content:", error);
    throw error;
  }
};

// Publish stored content
export const publishContent = async (
  persona_id: string,
  content_id: string,
): Promise<{
  content_id: string;
  publish_info: any;
  title: string;
  status: string;
}> => {
  try {
    const response = await apiClient.post(
      `/api/content/publish/${content_id}`,
      {
        persona_id,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error publishing content:", error);
    throw error;
  }
};

// Delete content
export const deleteContent = async (
  content_id: string,
): Promise<{ content_id: string; status: string }> => {
  try {
    const response = await apiClient.delete(
      `/api/content/content/${content_id}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting content:", error);
    throw error;
  }
};

// Get list of content for a persona
export const getContentList = async (
  persona_id: string,
  status?: "draft" | "published" | null,
): Promise<ContentResponse> => {
  try {
    const response = await apiClient.post("/api/content/list", {
      persona_id,
      cursor: undefined,
      limit: 50,
      status,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching content list:", error);
    throw error;
  }
};

export const contentQueries = {
  contentList: (
    persona_id: string,
    filters: { status?: "draft" | "published" | null } = {},
  ) =>
    queryOptions({
      queryKey: ["content", "list", persona_id, filters],
      queryFn: () => getContentList(persona_id, filters.status),
    }),
};

export function useEditContentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      personaId,
      contentId,
      content,
    }: {
      personaId: string;
      contentId: string;
      content: ContentEdit;
    }) => editContent(personaId, contentId, content),
    onSuccess: async (_, { personaId }) => {
      await queryClient.invalidateQueries(
        contentQueries.contentList(personaId),
      );
    },
  });
}

export function useEditContentWithFeedbackMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      personaId,
      contentId,
      content,
      feedback,
    }: {
      personaId: string;
      contentId: string;
      content: ContentFeedbackEdit["content_data"];
      feedback: string;
    }) =>
      editContentWithFeedback(personaId, contentId, {
        user_feedback: feedback,
        content_data: content,
      }),
    onSuccess: async (_, { personaId }) => {
      await queryClient.invalidateQueries(
        contentQueries.contentList(personaId),
      );
    },
  });
}

export function useSaveEditedContentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      personaId,
      contentId,
      saveOptions,
    }: {
      personaId: string;
      contentId: string;
      saveOptions?: ContentSaveOptions;
    }) => saveEditedContent(personaId, contentId, saveOptions),
    onSuccess: async (_, { personaId }) => {
      await queryClient.invalidateQueries(
        contentQueries.contentList(personaId),
      );
    },
  });
}

export function useDeleteContentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contentId }: { personaId: string; contentId: string }) =>
      deleteContent(contentId),
    onSuccess: async (_, { personaId }) => {
      await queryClient.invalidateQueries(
        contentQueries.contentList(personaId),
      );
    },
    onError: (error) => console.error("Delete error:", error),
  });
}

export function usePublishContentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      personaId,
      contentId,
    }: {
      personaId: string;
      contentId: string;
    }) => publishContent(personaId, contentId),
    onSuccess: async (_, { personaId }) => {
      await queryClient.invalidateQueries(
        contentQueries.contentList(personaId),
      );
    },
    onError: (error) => console.error("Publishing error:", error),
  });
}

export function useCreateContentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      variables: {
        personaId: string;
      } & (
        | {
            type: "media";
            content: MediaContentCreate;
          }
        | { type: "topic"; content: TopicContentCreate }
      ),
    ) =>
      variables.type === "media"
        ? createMediaContent(variables.personaId, variables.content)
        : createTopicContent(variables.personaId, variables.content),
    onSuccess: async (_, { personaId }) => {
      await queryClient.invalidateQueries(
        contentQueries.contentList(personaId),
      );
    },
    onError: (error) => {
      console.error("Content creation error:", error);
    },
  });
}
